import {
  ApiError,
  Button,
  ButtonColor,
  Field,
  FormError,
  messages,
  SelectControl,
  StaticValue,
  stringCompare,
  useAutoResetForm,
  useHasAccess,
  useSetApiErrors,
  useToastManager
} from "@quortex/front-common"
import { usePrompt } from "@tanstack/react-location"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { Controller, FormProvider } from "react-hook-form"
import { useStream, useUpdateStream, useVirtualAssetsList } from "src/queries/playStreams"
import { longRefetchOptions } from "@/App/play/helpers/input"

type FillersSchema = {
  virtual: {
    advertisementFiller: string | null
    defaultFiller: string | null
    gapFiller: string | null
  }
}

const getDefaultValues = (stream: api.ott.InputStream) => ({
  virtual: {
    advertisementFiller: stream.virtual?.advertisementFiller,
    defaultFiller: stream.virtual?.defaultFiller,
    gapFiller: stream.virtual?.gapFiller
  }
})

const FillersTab = () => {
  const { data: stream } = useSuspenseQuery(useStream())
  const defaultValues = useMemo(() => getDefaultValues(stream), [stream])
  const form = useAutoResetForm<FillersSchema>({ defaultValues })
  const { handleSubmit, formState, reset, watch } = form
  const { isDirty, isSubmitting } = formState
  const { mutateAsync: updateStream } = useMutation(useUpdateStream())
  const setApiErrors = useSetApiErrors(form)
  const hasWriteAccess = useHasAccess(["owner", "editor"])
  const toastManager = useToastManager()

  const { data: assets } = useSuspenseQuery({
    ...useVirtualAssetsList(),
    ...longRefetchOptions,
    select: data => data.results
  })
  const sortedAssets = useMemo(
    () => [...assets].sort((p1, p2) => stringCompare(p1.name, p2.name)),
    [assets]
  )

  const onSubmit = handleSubmit(async data => {
    try {
      const result = await updateStream(data)
      reset(getDefaultValues(result))
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        setApiErrors(e, data)
      } else {
        toastManager.error({ apiError: e })
      }
    }
  })

  const handleClickCancel = () => reset(getDefaultValues(stream))

  const defaultFillerUuid = watch("virtual.defaultFiller")
  const advertisementFillerUuid = watch("virtual.advertisementFiller")
  const gapFillerUuid = watch("virtual.gapFiller")

  const assetOptions = sortedAssets
    .filter(asset => asset.probe.state === "ok")
    .map(asset => ({ label: asset.name, value: asset.uuid }))

  usePrompt(messages.en.prompts.unsavedChanges, isDirty)

  return (
    <form noValidate onSubmit={onSubmit}>
      <FormProvider {...form}>
        <Field
          description="(Optional)"
          label="Advertisement filler"
          name="virtual.advertisementFiller"
        >
          {name => (
            <div className="flex flex-col">
              <p className="mb-xs text-grey-700">
                This filler is played during advertisement if no other asset is scheduled.
              </p>

              {hasWriteAccess ? (
                <Controller
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <SelectControl {...field} isClearable options={assetOptions} width="large" />
                  )}
                />
              ) : (
                <StaticValue
                  name={name}
                  value={sortedAssets.find(asset => asset.uuid === advertisementFillerUuid)?.name}
                />
              )}
            </div>
          )}
        </Field>

        <Field description="(Optional)" label="Gap filler" name="virtual.gapFiller">
          {name => (
            <div className="flex flex-col">
              <p className="mb-xs text-grey-700">
                This filler is played when there is a gap between two programs.
              </p>

              {hasWriteAccess ? (
                <Controller
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <SelectControl {...field} isClearable options={assetOptions} width="large" />
                  )}
                />
              ) : (
                <StaticValue
                  name={name}
                  value={sortedAssets.find(asset => asset.uuid === gapFillerUuid)?.name}
                />
              )}
            </div>
          )}
        </Field>

        <Field description="(Optional)" label="Default filler" name="virtual.defaultFiller">
          {name => (
            <div className="flex flex-col">
              <p className="mb-xs text-grey-700">
                We will stream this video when there is no other content to show.
              </p>

              {hasWriteAccess ? (
                <Controller
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <SelectControl {...field} isClearable options={assetOptions} width="large" />
                  )}
                />
              ) : (
                <StaticValue
                  name={name}
                  value={sortedAssets.find(asset => asset.uuid === defaultFillerUuid)?.name}
                />
              )}
            </div>
          )}
        </Field>

        <FormError className="mt-md" />

        <div className="mt-sm">
          {hasWriteAccess && isDirty && (
            <div className="mt-lg flex justify-end space-x-3">
              <Button color={ButtonColor.SECONDARY} type="button" onClick={handleClickCancel}>
                Cancel
              </Button>
              <Button color={ButtonColor.PRIMARY} isLoading={isSubmitting} type="submit">
                Save
              </Button>
            </div>
          )}
        </div>
      </FormProvider>
    </form>
  )
}

export default FillersTab
